import * as React from "react";
import { FormattedMessage, useIntl } from "@/i18n/i18n-client";
import { Field, Form } from "react-final-form";
import Asterisk from "@/shared/globals/UiElements/Asterisk";
import { useStore } from "@/lib/storeData/StoreContext";
import { PrimaryButton } from "@/components/Button";
import { EnvelopeOutlined } from "@/assets/Icons/EnvelopeOutlined";
import { PhoneOutlined } from "@/assets/Icons/PhoneOutlined";
import { MarkerOutlined } from "@/assets/Icons/MarkerOutlined";
import { Label } from "@/shared/globals";
import { Card } from "@/shared/globals/UiElements/Card";
import { RequiredSpan, StyledH3 } from "./styled";
import { SocialLinks } from "@/components/UtilityComponents/SocialLinks";
import { useSubmitStoreContactFormMutation } from "@/generated/graphql";
import { PhoneInput } from "@/shared/globals/UiElements/InputPhone";
import Flex from "@/shared/globals/UiElements/Flex";
import { Input, TextArea } from "../../components/Input";
import InputPhone from "../../components/Input/inputPhone";
import { fireSubmitContactFormEvent } from "./utils";
import { FormApi } from "final-form";
import { CheckmarkCircleIcon } from "@/assets/Icons/CheckmarkCircleIcon";
import { AlertOctagonIcon } from "@/assets/Icons/AlertOctagonIcon";

export type ContactFormValues = {
  name: string;
  email: string;
  phone: PhoneInput;
  subjectTitle: string;
  subjectBody: string;
};

const SuccessMessage = () => (
  <div className="flex items-center bg-success-50 rounded-lg border border-success-200 p-2 gap-2xs">
    <CheckmarkCircleIcon />
    <span className="text-text-xs font-medium text-success-800">
      <FormattedMessage
        defaultMessage="We have received your message and will reach you as soon as possible."
        id="CxNsYA"
      />
    </span>
  </div>
);

const FailureMessage = () => (
  <div className="flex items-center bg-error-50 rounded-lg border border-error-200 p-2 gap-2xs">
    <AlertOctagonIcon />
    <span className="text-text-xs font-medium text-error-800">
      <FormattedMessage
        defaultMessage="Something went wrong, please try again."
        id="8GvVWZ"
      />
    </span>
  </div>
);

const Contact = () => {
  const intl = useIntl();
  const { storeId, socialMedia, contactInfo, location } = useStore();
  const [isSuccess, setIsSuccess] = React.useState<boolean | undefined>(
    undefined
  );
  const [SubmitStoreContactForm] = useSubmitStoreContactFormMutation();

  const email = contactInfo?.email;
  const phone = contactInfo?.phone;
  const address = location?.address;

  const handleSubmitContact = async (
    input: ContactFormValues,
    form: FormApi<ContactFormValues>
  ) => {
    try {
      const { data } = await SubmitStoreContactForm({
        variables: {
          input: {
            storeId,
            name: input.name,
            fromEmail: input.email,
            phone: input?.phone?.value!,
            messageSubject: input.subjectTitle,
            messageBody: input.subjectBody,
          },
        },
      });

      if (data?.submitStoreContactForm?.statusCode! < 400) {
        handleFeedback(true);
        fireSubmitContactFormEvent(input);
        // reset the form and prevent the fields validation from being triggered
        form.reset();
        form.resetFieldState("name");
        form.resetFieldState("email");
        form.resetFieldState("phone");
        form.resetFieldState("subjectTitle");
        form.resetFieldState("subjectBody");
      } else {
        handleFeedback(false);
      }
    } catch (error) {
      handleFeedback(false);
    }
  };

  const handleFeedback = (value: boolean) => {
    setIsSuccess(value);
  };

  const isContactInfoExist = !!(email || phone || address);
  const socialLinkContent = socialMedia?.length ? (
    <>
      <hr className="w-full my-xl border-[#C4CDD5]" />
      <Flex column>
        <h2 className="text-center text-sm font-normal w-full">
          <FormattedMessage
            defaultMessage="You can also follow & contact us through"
            id="Q8HNEo"
          />
        </h2>
        <SocialLinks
          className="!w-full"
          justifyContent="center"
          socialSettings={socialMedia}
        />
      </Flex>
    </>
  ) : null;

  return (
    <div className="py-2xl w-full">
      <Flex
        justifyContent={isContactInfoExist ? "space-between " : "center"}
        spacing="xxxl"
        rotateOn="TABLET"
      >
        <Card
          className={`justify-start items-start ${
            isContactInfoExist ? "flex-[2]" : "md:max-w-[500px] w-full"
          }`}
          paddingSize="xl"
        >
          <Flex column>
            <h4 className={"text-gray-600 text-center"}>
              <FormattedMessage
                defaultMessage="You can send your questions by filling out the form below & We will get back to you as soon as possible."
                id="STHKir"
              />
            </h4>
            <Flex>
              <Form<ContactFormValues>
                onSubmit={(values, form) => handleSubmitContact(values, form)}
                validate={validate}
                initialValues={{}}
                // decorators={[focusOnError]}
                render={({ handleSubmit, submitting }) => {
                  return (
                    <form className="w-full" onSubmit={handleSubmit}>
                      <Label>
                        <Flex spacing="xxs">
                          <FormattedMessage defaultMessage="Name" id="HAlOn1" />
                          <Asterisk />
                        </Flex>
                        <Field name="name">
                          {({ input, meta: { error, touched } }) => (
                            <>
                              <Input
                                {...input}
                                className={error && touched ? "invalid" : ""}
                                placeholder={intl.formatMessage({
                                  defaultMessage: "Enter your name",
                                  id: "DX9QSt",
                                })}
                                autoFocus={false}
                              />
                              {error && touched && (
                                <RequiredSpan>{error}</RequiredSpan>
                              )}
                            </>
                          )}
                        </Field>
                      </Label>
                      <Label>
                        <Flex spacing="xxs">
                          <FormattedMessage
                            defaultMessage="Email"
                            id="sy+pv5"
                          />
                          <Asterisk />
                        </Flex>
                        <Field name="email">
                          {({ input, meta: { error, touched } }) => (
                            <>
                              <Input
                                {...input}
                                className={error && touched ? "invalid" : ""}
                                type="email"
                                placeholder={intl.formatMessage({
                                  defaultMessage: "Enter your email",
                                  id: "5MDGuM",
                                })}
                                autoFocus={false}
                              />
                              {error && touched && (
                                <RequiredSpan>{error}</RequiredSpan>
                              )}
                            </>
                          )}
                        </Field>
                      </Label>
                      <Label>
                        <Flex spacing="xxs">
                          <FormattedMessage
                            defaultMessage="Phone number"
                            id="jdJhOL"
                          />
                          <Asterisk />
                        </Flex>
                        <Field<PhoneInput> name="phone">
                          {(fieldProps) => (
                            <InputPhone
                              {...fieldProps}
                              dataTest="type-primary-phone"
                              className=""
                            />
                          )}
                        </Field>
                      </Label>
                      <Label>
                        <Flex spacing="xxs">
                          <FormattedMessage
                            defaultMessage="Subject"
                            id="LLtKhp"
                          />
                          <Asterisk />
                        </Flex>
                        <Field name="subjectTitle">
                          {({ input, meta: { error, touched } }) => (
                            <>
                              <Input
                                {...input}
                                className={error && touched ? "invalid" : ""}
                                placeholder={intl.formatMessage({
                                  defaultMessage: "Enter your subject",
                                  id: "WpaspK",
                                })}
                              />
                              {error && touched && (
                                <RequiredSpan>{error}</RequiredSpan>
                              )}
                            </>
                          )}
                        </Field>
                      </Label>
                      <Label>
                        <Flex spacing="xxs">
                          <FormattedMessage
                            defaultMessage="Subject Body"
                            id="JWJjnE"
                          />
                          <Asterisk />
                        </Flex>
                        <Field name="subjectBody">
                          {({ input, meta: { error, touched } }) => (
                            <>
                              <TextArea
                                {...input}
                                className={error && touched ? "invalid" : ""}
                                placeholder={intl.formatMessage({
                                  defaultMessage: "Write your message...",
                                  id: "yoP6t+",
                                })}
                              />
                              {error && touched && (
                                <RequiredSpan>{error}</RequiredSpan>
                              )}
                            </>
                          )}
                        </Field>
                      </Label>
                      <div className="flex flex-col gap-2 justify-end">
                        <PrimaryButton
                          type="submit"
                          className="!mt-6 !rounded-[3.125rem]"
                          fullWidth
                          isLoading={submitting}
                        >
                          <FormattedMessage defaultMessage="Send" id="9WRlF4" />
                        </PrimaryButton>
                        {isSuccess === true ? (
                          <SuccessMessage />
                        ) : isSuccess === false ? (
                          <FailureMessage />
                        ) : null}
                      </div>
                    </form>
                  );
                }}
              />
            </Flex>
          </Flex>
          {!isContactInfoExist && socialLinkContent}
        </Card>

        {isContactInfoExist && (
          <Card className="w-full flex-1 h-fit" paddingSize="xl">
            <Flex column spacing="xxl">
              {email && (
                <Flex>
                  <div className="min-w-5">
                    <EnvelopeOutlined />
                  </div>
                  <Flex column alignItems="flex-start" spacing="xs">
                    <p className="text-gray-600 text-xs m-none">
                      <FormattedMessage defaultMessage="Email" id="sy+pv5" />
                    </p>
                    <StyledH3>{email}</StyledH3>
                  </Flex>
                </Flex>
              )}
              {phone && (
                <Flex>
                  <div className="min-w-5">
                    <PhoneOutlined />
                  </div>
                  <Flex column alignItems="flex-start" spacing="xs">
                    <p className="text-gray-600 text-xs m-none">
                      <FormattedMessage defaultMessage="Phone" id="O95R3Z" />
                    </p>
                    <StyledH3>{phone}</StyledH3>
                  </Flex>
                </Flex>
              )}
              {address && (
                <Flex>
                  <div className="min-w-5">
                    <MarkerOutlined />
                  </div>
                  <Flex column alignItems="flex-start" spacing="xs">
                    <p className="text-gray-600 text-xs m-none">
                      <FormattedMessage defaultMessage="Address" id="e6Ph5+" />
                    </p>
                    <StyledH3>{address}</StyledH3>
                  </Flex>
                </Flex>
              )}
            </Flex>
            {socialLinkContent}
          </Card>
        )}
      </Flex>
    </div>
  );
};

const validate = (values: ContactFormValues) => {
  const errors: Record<string, unknown> = {};

  if (!values.name?.trim()) {
    errors.name = (
      <FormattedMessage defaultMessage="Enter your full name" id="xIDmuE" />
    );
  }

  if (
    !values?.email ||
    !values?.email?.match(
      /^([a-zA-Z0-9_.-])+@(([a-zA-Z0-9-])+\.)+([a-zA-Z0-9]{2,4})+$/
    )
  ) {
    errors.email = (
      <FormattedMessage
        defaultMessage="Enter a valid email address"
        id="YRe93Y"
      />
    );
  }

  if (!values.phone?.isValid) {
    errors.phone = (
      <FormattedMessage
        defaultMessage="Enter a valid phone number"
        id="iydw3u"
      />
    );
  }

  if (!values.subjectTitle?.trim()) {
    errors.subjectTitle = (
      <FormattedMessage defaultMessage="Enter subject title" id="KkreSs" />
    );
  }

  if (!values.subjectBody?.trim()) {
    errors.subjectBody = (
      <FormattedMessage defaultMessage="Enter subject content" id="ND8eVa" />
    );
  }
  return errors;
};

export default Contact;
